import { z } from "zod";

// Point Zod Schema - inline to avoid importing Sequelize models
const pointZodSchema = z.object({
	pointId: z.number().int().positive().optional(),
	memberId: z.number().int().positive(),
	visitId: z.number().int().positive().optional().nullable(),
	point: z.number().int(),
	processedAt: z.date().optional(),
	source: z
		.enum(["qr-visit", "barcode", "manual", "admin", "import"])
		.optional()
		.nullable(),
	createdAt: z.date().optional(),
	updatedAt: z.date().optional(),
});

/**
 * Member Point API Schemas
 * Following Standard Schema V1 principles and API Schema Rubric
 */

// ============================================================================
// Path Parameters
// ============================================================================

export const updateMemberPointsPathParamsSchema = z.object({
	memberId: z.coerce.string().describe("ID of member to update points"),
});

export const getMemberPointHistoryPathParamsSchema = z.object({
	memberId: z.coerce.string().describe("ID of member to get history"),
});

export const getDailyPointSummaryPathParamsSchema = z.object({
	memberId: z.coerce.string().describe("ID of member to get summary"),
});

// ============================================================================
// Query Parameters
// ============================================================================

export const getMemberPointHistoryQueryParamsSchema = z
	.object({
		limit: z.coerce
			.number()
			.int()
			.min(1)
			.max(100)
			.default(20)
			.describe("Query parameter for limit")
			.optional(),
		offset: z.coerce
			.number()
			.int()
			.min(0)
			.default(0)
			.describe("Query parameter for offset")
			.optional(),
		source: z
			.enum(["qr-visit", "barcode", "manual", "admin", "import"])
			.describe("Query parameter for point source filter")
			.optional(),
		startDate: z.coerce
			.string()
			.optional()
			.describe("Query parameter for minimum transaction date"),
		endDate: z.coerce
			.string()
			.optional()
			.describe("Query parameter for maximum transaction date"),
	})
	.optional();

export const getDailyPointSummaryQueryParamsSchema = z
	.object({
		startDate: z.coerce
			.string()
			.optional()
			.describe("Query parameter for summary start date"),
		endDate: z.coerce
			.string()
			.optional()
			.describe("Query parameter for summary end date"),
	})
	.optional();

// ============================================================================
// Request Bodies
// ============================================================================

export const updateMemberPointsMutationRequestSchema = z.object({
	pointAmount: z.coerce
		.number()
		.int()
		.positive()
		.describe("Amount of points to add or deduct"),
	isAdd: z.coerce.boolean().describe("Whether to add (true) or deduct (false)"),
	source: z
		.enum(["qr-visit", "barcode", "manual", "admin", "import"])
		.optional()
		.describe("Source of the point transaction"),
	visitId: z.coerce
		.number()
		.int()
		.positive()
		.optional()
		.describe("Associated visit ID if applicable"),
	reason: z.coerce
		.string()
		.optional()
		.describe("Reason for manual point adjustment"),
});

export const bulkUpdateMemberPointsMutationRequestSchema = z.object({
	updates: z.array(
		z.object({
			memberId: z.coerce
				.number()
				.int()
				.positive()
				.describe("Member ID for point update"),
			pointAmount: z.coerce
				.number()
				.int()
				.positive()
				.describe("Amount of points"),
			isAdd: z.coerce.boolean().describe("Add or deduct points"),
			reason: z.coerce.string().optional().describe("Reason for adjustment"),
		}),
	),
});

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Point with relations schema
 */
export const pointWithRelationsZodSchema = pointZodSchema.extend({
	Member: z.any().optional().describe("Associated member data"),
	Visit: z.any().optional().describe("Associated visit data"),
});

/**
 * Daily point summary schema
 */
export const dailyPointSummaryZodSchema = z.object({
	date: z.coerce.string().describe("Summary date"),
	pointsEarned: z.coerce.number().int().describe("Points earned on date"),
	pointsSpent: z.coerce.number().int().describe("Points spent on date"),
	transactionCount: z.coerce.number().int().describe("Transactions on date"),
	runningBalance: z.coerce.number().int().describe("Balance at end of date"),
});

/**
 * @description Member points updated successfully
 */
export const updateMemberPoints200Schema = z.object({
	pointTransaction: pointZodSchema,
	newBalance: z.coerce.number().int().describe("Updated point balance"),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
	message: z.coerce.string(),
});

/**
 * @description Point history retrieved successfully
 */
export const getMemberPointHistory200Schema = z.object({
	transactions: z.array(pointWithRelationsZodSchema),
	total: z.coerce.number().int().describe("Total number of transactions"),
	currentBalance: z.coerce.number().int().describe("Current point balance"),
	limit: z.coerce.number().int().describe("Limit applied to query"),
	offset: z.coerce.number().int().describe("Offset applied to query"),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
	message: z.coerce.string(),
});

/**
 * @description Daily point summary retrieved successfully
 */
export const getDailyPointSummary200Schema = z.object({
	summary: z.array(dailyPointSummaryZodSchema),
	totalDays: z.coerce.number().int().describe("Number of days in summary"),
	startDate: z.coerce.string().describe("Summary start date"),
	endDate: z.coerce.string().describe("Summary end date"),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
	message: z.coerce.string(),
});

/**
 * @description Bulk point updates completed successfully
 */
export const bulkUpdateMemberPoints200Schema = z.object({
	processedUpdates: z.array(
		z.object({
			memberId: z.coerce.number().int(),
			pointTransaction: pointZodSchema,
			newBalance: z.coerce.number().int(),
		}),
	),
	successCount: z.coerce
		.number()
		.int()
		.describe("Number of successful updates"),
	failureCount: z.coerce.number().int().describe("Number of failed updates"),
	errors: z.array(z.string()).optional().describe("Error messages if any"),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
	message: z.coerce.string(),
});

// ============================================================================
// Error Response Schemas
// ============================================================================

/**
 * Standard response schema for errors
 */
export const pointResponseSchema = z.object({
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
});

/**
 * @description Bad request
 */
export const updateMemberPoints400Schema = z.lazy(() => pointResponseSchema);
export const getMemberPointHistory400Schema = z.lazy(() => pointResponseSchema);
export const getDailyPointSummary400Schema = z.lazy(() => pointResponseSchema);
export const bulkUpdateMemberPoints400Schema = z.lazy(
	() => pointResponseSchema,
);

/**
 * @description Member not found
 */
export const updateMemberPoints404Schema = z.lazy(() => pointResponseSchema);
export const getMemberPointHistory404Schema = z.lazy(() => pointResponseSchema);
export const getDailyPointSummary404Schema = z.lazy(() => pointResponseSchema);

/**
 * @description Insufficient points
 */
export const updateMemberPoints422Schema = z.lazy(() => pointResponseSchema);

/**
 * @description Internal server error
 */
export const updateMemberPoints500Schema = z.lazy(() => pointResponseSchema);
export const getMemberPointHistory500Schema = z.lazy(() => pointResponseSchema);
export const getDailyPointSummary500Schema = z.lazy(() => pointResponseSchema);
export const bulkUpdateMemberPoints500Schema = z.lazy(
	() => pointResponseSchema,
);

// ============================================================================
// Convenience Aliases
// ============================================================================

export const updateMemberPointsMutationResponseSchema = z.lazy(
	() => updateMemberPoints200Schema,
);
export const getMemberPointHistoryQueryResponseSchema = z.lazy(
	() => getMemberPointHistory200Schema,
);
export const getDailyPointSummaryQueryResponseSchema = z.lazy(
	() => getDailyPointSummary200Schema,
);
export const bulkUpdateMemberPointsMutationResponseSchema = z.lazy(
	() => bulkUpdateMemberPoints200Schema,
);
